export const TRACKING_PARAM_NAMES = [
  "gclid",
  "gbraid",
  "wbraid",
  "gad_source",
  "gad_campaignid",
  "utm_id",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "utm_source_platform",
];

const unsupportedHrefPattern = /^(?:mailto:|tel:|javascript:)/i;

function getSearchParams(search) {
  if (!search) {
    return new URLSearchParams();
  }

  if (search instanceof URLSearchParams) {
    return new URLSearchParams(search);
  }

  return new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
}

function getLocationOrigin(locationLike) {
  if (locationLike?.origin) {
    return locationLike.origin;
  }

  try {
    return new URL(locationLike?.href).origin;
  } catch {
    return 'http://localhost';
  }
}

function getLocationPathname(locationLike) {
  if (locationLike?.pathname) {
    return locationLike.pathname;
  }

  try {
    return new URL(locationLike?.href).pathname;
  } catch {
    return '/';
  }
}

function getLocationSearch(locationLike) {
  if (typeof locationLike?.search === 'string') {
    return locationLike.search;
  }

  try {
    return new URL(locationLike?.href).search;
  } catch {
    return '';
  }
}

function isHashOnlyHref(href) {
  return href.startsWith('#');
}

function createTargetUrl(href, locationLike) {
  if (isHashOnlyHref(href)) {
    const pathname = getLocationPathname(locationLike);
    const search = getLocationSearch(locationLike);

    return new URL(`${pathname}${search}${href}`, getLocationOrigin(locationLike));
  }

  return new URL(href, getLocationOrigin(locationLike));
}

function formatTargetHref(targetUrl, originalHref, currentOrigin) {
  if (/^https?:\/\//i.test(originalHref)) {
    return targetUrl.toString();
  }

  if (targetUrl.origin !== currentOrigin) {
    return targetUrl.toString();
  }

  return `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
}

export function hasTrackingParams(search) {
  const sourceParams = getSearchParams(search);

  return TRACKING_PARAM_NAMES.some(
    (name) => sourceParams.has(name) && sourceParams.get(name) !== '',
  );
}

export function mergeTrackingParams(currentParams, search) {
  const nextParams = getSearchParams(currentParams);
  const sourceParams = getSearchParams(search);

  for (const name of TRACKING_PARAM_NAMES) {
    const value = sourceParams.get(name);

    if (value && !nextParams.has(name)) {
      nextParams.set(name, value);
    }
  }

  return nextParams;
}

export function createTrackingSearch(params) {
  const trackingParams = getSearchParams(params);

  return trackingParams.toString() ? `?${trackingParams.toString()}` : '';
}

export function appendTrackingParamsToHref(
  href,
  trackingParams,
  locationLike = globalThis.window?.location,
  options = {},
) {
  if (!href || unsupportedHrefPattern.test(href)) {
    return href;
  }

  if (!hasTrackingParams(trackingParams)) {
    return href;
  }

  let targetUrl;
  const currentOrigin = getLocationOrigin(locationLike);

  try {
    targetUrl = createTargetUrl(href, locationLike);
  } catch {
    return href;
  }

  if (targetUrl.origin !== currentOrigin && options.allowExternal !== true) {
    return href;
  }

  const sourceParams = getSearchParams(trackingParams);
  let changed = false;

  for (const name of TRACKING_PARAM_NAMES) {
    const value = sourceParams.get(name);

    if (value && !targetUrl.searchParams.has(name)) {
      targetUrl.searchParams.set(name, value);
      changed = true;
    }
  }

  if (!changed) {
    return href;
  }

  return formatTargetHref(targetUrl, href, currentOrigin);
}
